import { supabase } from '../supabase';
import { buildThemePreviewUrl } from '../utils/themePreview';
import {
  CUSTOM_THEME_ID,
  THEME_CATALOG,
  getThemeMeta,
  isCustomThemeId,
  slugifyCustomThemeName,
} from '../../../themes/registry';
import { getThemeCodebasePath } from '../../../themes/by-restaurant/restaurant-theme-env';

const DEFAULT_PLATFORM_ID = 'a';
const THEME_QUERY_TIMEOUT_MS = 15000;

class ThemeService {
  constructor() {
    this.DEFAULT_PLATFORM_ID = DEFAULT_PLATFORM_ID;
    this.THEME_QUERY_TIMEOUT_MS = THEME_QUERY_TIMEOUT_MS;
    this.CUSTOM_THEME_ID = CUSTOM_THEME_ID;
  }

  /**
   * Return default catalog options.
   * @private
   */
  _catalogFallbackOptions() {
    return THEME_CATALOG.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      kind: 'platform',
      disabled: false,
    }));
  }

  /**
   * Resolve Custom Theme ID value.
   * @private
   */
  _resolveCustomThemeIdForOptions(selectionId) {
    if (isCustomThemeId(selectionId)) return selectionId;
    return this.CUSTOM_THEME_ID;
  }

  /**
   * Fetch admin-enabled theme options for one restaurant.
   * Both queries are time-bounded: a stalled request must degrade to the
   * catalog fallback instead of leaving pickers on a spinner forever.
   */
  async getThemeOptionsForRestaurant(restaurantId) {
    if (!restaurantId) return this._catalogFallbackOptions();

    const [optionsResult, customResult] = await Promise.all([
      this._queryWithTimeout(
        (signal) =>
          this._withAbortSignal(
            supabase
              .from('restaurant_theme_options')
              .select('theme_kind, theme_id, enabled, is_default')
              .eq('restaurant_id', restaurantId)
              .eq('enabled', true)
              .order('theme_kind')
              .order('theme_id'),
            signal,
          ),
        'Theme options',
        10000,
      ),
      this._queryWithTimeout(
        (signal) =>
          this._withAbortSignal(
            supabase
              .from('restaurant_custom_themes')
              .select('id, name, description, preview_url, status, slug')
              .eq('restaurant_id', restaurantId)
              .order('created_at'),
            signal,
          ),
        'Custom themes',
        10000,
      ),
    ]);

    if (optionsResult.error) {
      console.warn('[themeService] restaurant_theme_options:', optionsResult.error.message);
      return this._catalogFallbackOptions();
    }

    const rows = optionsResult.data || [];
    if (rows.length === 0) return this._catalogFallbackOptions();

    const customById = {};
    (customResult.data || []).forEach((c) => {
      customById[c.id] = c;
    });

    const pickerOptions = [];

    for (const row of rows) {
      if (row.theme_kind === 'platform') {
        const meta = getThemeMeta(row.theme_id);
        pickerOptions.push({
          id: row.theme_id,
          name: meta.name,
          description: meta.description,
          kind: 'platform',
          disabled: false,
          previewUrl: buildThemePreviewUrl(restaurantId, row.theme_id),
        });
        continue;
      }

      if (row.theme_kind === 'custom') {
        const customMeta = customById[row.theme_id];
        const building = customMeta?.status === 'building';
        pickerOptions.push({
          id: row.theme_id,
          name: customMeta?.name || 'Custom App',
          description:
            customMeta?.description ||
            (building
              ? 'Your branded app is being prepared.'
              : 'Bespoke customer experience for this restaurant.'),
          kind: 'custom',
          disabled: building || customMeta?.status === 'retired' || !customMeta,
          badge: 'Built for you',
          previewUrl: customMeta?.id ? buildThemePreviewUrl(restaurantId, customMeta.id) : null,
          slug: customMeta?.slug || null,
        });
      }
    }

    return pickerOptions.length > 0 ? pickerOptions : this._catalogFallbackOptions();
  }

  /** Check whether a theme selection is allowed for this restaurant. */
  async isThemeSelectionAllowed(restaurantId, selectionId, themeKind) {
    const themeIdForRow =
      themeKind === 'custom'
        ? this._resolveCustomThemeIdForOptions(selectionId)
        : selectionId || this.DEFAULT_PLATFORM_ID;

    const { data, error } = await supabase
      .from('restaurant_theme_options')
      .select('id')
      .eq('restaurant_id', restaurantId)
      .eq('theme_kind', themeKind)
      .eq('theme_id', themeIdForRow)
      .eq('enabled', true)
      .maybeSingle();

    if (error) {
      console.warn('[themeService] isThemeSelectionAllowed:', error.message);
      return themeKind === 'platform' && selectionId === this.DEFAULT_PLATFORM_ID;
    }

    return Boolean(data);
  }

  /** Save active theme on restaurants row (owner/manager). */
  async saveRestaurantThemeSelection(restaurantId, selectionId, themeKind) {
    const theme_id =
      themeKind === 'custom' ? this._resolveCustomThemeIdForOptions(selectionId) : selectionId;
    const theme_type = themeKind;

    const allowed = await this.isThemeSelectionAllowed(restaurantId, selectionId, themeKind);
    if (!allowed) {
      return { error: new Error('This theme is not available for your restaurant.'), data: null };
    }

    const { data: rpcRows, error: rpcError } = await supabase.rpc('save_restaurant_theme', {
      p_restaurant_id: restaurantId,
      p_theme_id: theme_id,
      p_theme_type: theme_type,
    });

    if (!rpcError && rpcRows) {
      const rows = Array.isArray(rpcRows) ? rpcRows : [rpcRows];
      const row = rows[0];
      if (row?.theme_id && row?.theme_type) {
        return { error: null, data: { theme_id: row.theme_id, theme_type: row.theme_type } };
      }
    }

    if (rpcError && !rpcError.message?.includes('Could not find the function')) {
      console.warn('[themeService] save_restaurant_theme RPC:', rpcError.message);
      return { error: rpcError, data: null };
    }

    const { data, error } = await supabase
      .from('restaurants')
      .update({
        theme_id,
        theme_type,
        updated_at: new Date().toISOString(),
      })
      .eq('id', restaurantId)
      .select('theme_id, theme_type');

    if (error) {
      return { error, data: null };
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (row?.theme_id === theme_id && row?.theme_type === theme_type) {
      return { error: null, data: row };
    }

    const { data: verify, error: verifyError } = await supabase
      .from('restaurants')
      .select('theme_id, theme_type')
      .eq('id', restaurantId)
      .maybeSingle();

    if (verifyError) {
      return { error: verifyError, data: null };
    }

    if (verify?.theme_id === theme_id && verify?.theme_type === theme_type) {
      return { error: null, data: verify };
    }

    return {
      error: new Error('Theme update did not persist. Please try again.'),
      data: null,
    };
  }

  /** Load custom theme slug for guest app bundle resolution. */
  async getCustomThemeSlug(restaurantId, customThemeId) {
    if (!restaurantId || !customThemeId || customThemeId === this.CUSTOM_THEME_ID) {
      return null;
    }

    const { data, error } = await supabase
      .from('restaurant_custom_themes')
      .select('slug')
      .eq('restaurant_id', restaurantId)
      .eq('id', customThemeId)
      .maybeSingle();

    if (error || !data?.slug) return null;
    return data.slug;
  }

  /** Apply abort signal to query. */
  _withAbortSignal(query, signal) {
    if (signal) {
      return query.abortSignal(signal);
    }
    return query;
  }

  /** Check if abort error. */
  _isAbortError(error) {
    if (!error) return false;
    const message = typeof error === 'string' ? error : error.message || '';
    return (
      message.includes('AbortError') || message.includes('aborted') || error.name === 'AbortError'
    );
  }

  /** Query database with configurable timeout limits. */
  async _queryWithTimeout(runQuery, label, ms = this.THEME_QUERY_TIMEOUT_MS, externalSignal) {
    if (externalSignal?.aborted) {
      return { data: null, error: { message: `${label} aborted`, name: 'AbortError' } };
    }

    const controller = new AbortController();
    const abortQuery = () => controller.abort();

    if (externalSignal) {
      externalSignal.addEventListener('abort', abortQuery, { once: true });
    }

    let timer;
    try {
      const queryPromise = runQuery(controller.signal);
      const timeoutPromise = new Promise((resolve) => {
        timer = setTimeout(() => {
          controller.abort();
          resolve({
            data: null,
            error: { message: `${label} timed out after ${ms / 1000}s` },
          });
        }, ms);
      });

      const result = await Promise.race([queryPromise, timeoutPromise]);
      if (this._isAbortError(result?.error) && externalSignal?.aborted) {
        return { data: null, error: { message: `${label} aborted`, name: 'AbortError' } };
      }
      return result;
    } finally {
      clearTimeout(timer);
      if (externalSignal) {
        externalSignal.removeEventListener('abort', abortQuery);
      }
    }
  }

  /** Build default enabled platforms list. */
  _buildDefaultEnabledPlatform() {
    const enabledPlatform = {};
    THEME_CATALOG.forEach((t) => {
      enabledPlatform[t.id] = t.id === DEFAULT_PLATFORM_ID;
    });
    return enabledPlatform;
  }

  /** Default admin theme state when DB rows are missing or queries fail. */
  buildDefaultThemeAdminState() {
    return {
      enabledPlatform: this._buildDefaultEnabledPlatform(),
      customThemes: [],
      enabledCustom: {},
      defaultThemeId: DEFAULT_PLATFORM_ID,
      defaultThemeType: 'platform',
      errors: [],
    };
  }

  /** Load admin theme configuration state for one restaurant. */
  async getRestaurantThemeAdminState(restaurantId, { signal } = {}) {
    const defaults = this.buildDefaultThemeAdminState();

    if (signal?.aborted) {
      return { ...defaults, errors: [{ message: 'Request aborted' }] };
    }

    const [optionsResult, customResult, restaurantResult] = await Promise.all([
      this._queryWithTimeout(
        (querySignal) =>
          this._withAbortSignal(
            supabase.from('restaurant_theme_options').select('*').eq('restaurant_id', restaurantId),
            querySignal,
          ),
        'Theme options',
        this.THEME_QUERY_TIMEOUT_MS,
        signal,
      ),
      this._queryWithTimeout(
        (querySignal) =>
          this._withAbortSignal(
            supabase
              .from('restaurant_custom_themes')
              .select(
                'id, restaurant_id, name, slug, description, preview_url, status, codebase_url, created_at, updated_at',
              )
              .eq('restaurant_id', restaurantId)
              .order('created_at'),
            querySignal,
          ),
        'Custom themes',
        this.THEME_QUERY_TIMEOUT_MS,
        signal,
      ),
      this._queryWithTimeout(
        (querySignal) =>
          this._withAbortSignal(
            supabase
              .from('restaurants')
              .select('theme_id, theme_type')
              .eq('id', restaurantId)
              .maybeSingle(),
            querySignal,
          ),
        'Restaurant theme',
        this.THEME_QUERY_TIMEOUT_MS,
        signal,
      ),
    ]);

    if (signal?.aborted) {
      return { ...defaults, errors: [{ message: 'Request aborted' }] };
    }

    let customThemes = customResult.data || [];
    let customError = customResult.error;
    if (customError && !this._isAbortError(customError) && !customThemes.length) {
      const fallback = await this._queryWithTimeout(
        (querySignal) =>
          this._withAbortSignal(
            supabase
              .from('restaurant_custom_themes')
              .select(
                'id, restaurant_id, name, description, preview_url, status, codebase_url, created_at, updated_at',
              )
              .eq('restaurant_id', restaurantId),
            querySignal,
          ),
        'Custom themes (fallback)',
        this.THEME_QUERY_TIMEOUT_MS,
        signal,
      );
      if (!fallback.error) {
        customThemes = (fallback.data || []).map((row) => ({
          ...row,
          slug: row.slug || 'default',
        }));
        customError = null;
      } else if (!this._isAbortError(fallback.error)) {
        customError = fallback.error;
      }
    }

    const enabledPlatform = { ...defaults.enabledPlatform };
    (optionsResult.data || []).forEach((row) => {
      if (row.theme_kind === 'platform' && row.enabled) {
        enabledPlatform[row.theme_id] = true;
      }
    });

    if (!Object.values(enabledPlatform).some(Boolean) && !optionsResult.error) {
      enabledPlatform[DEFAULT_PLATFORM_ID] = true;
    }

    const enabledCustom = {};
    customThemes.forEach((ct) => {
      const opt = (optionsResult.data || []).find(
        (r) => r.theme_kind === 'custom' && r.theme_id === ct.id,
      );
      enabledCustom[ct.id] = Boolean(opt?.enabled);
    });

    const errors = [optionsResult.error, customError, restaurantResult.error].filter(
      (error) => error && !this._isAbortError(error),
    );

    return {
      enabledPlatform,
      customThemes,
      enabledCustom,
      defaultThemeId: restaurantResult.data?.theme_id || DEFAULT_PLATFORM_ID,
      defaultThemeType: restaurantResult.data?.theme_type || 'platform',
      errors,
      partial: errors.length > 0,
    };
  }

  /** Persist admin theme options for one restaurant. */
  async saveRestaurantThemeAdminState(restaurantId, payload) {
    const { enabledPlatform, customThemes, enabledCustom, defaultThemeId, defaultThemeType } =
      payload;

    const upserts = [];

    for (const entry of THEME_CATALOG) {
      const enabled = Boolean(enabledPlatform[entry.id]);
      upserts.push({
        restaurant_id: restaurantId,
        theme_kind: 'platform',
        theme_id: entry.id,
        enabled,
        is_default: defaultThemeType === 'platform' && defaultThemeId === entry.id,
        updated_at: new Date().toISOString(),
      });
    }

    const { error: upsertError } = await supabase
      .from('restaurant_theme_options')
      .upsert(upserts, { onConflict: 'restaurant_id,theme_kind,theme_id' });

    if (upsertError) return { error: upsertError };

    const keptCustomIds = [];

    for (let index = 0; index < (customThemes || []).length; index++) {
      const theme = customThemes[index];
      if (!theme?.name?.trim()) continue;

      const slug = theme.slug?.trim() || slugifyCustomThemeName(theme.name);
      const row = {
        restaurant_id: restaurantId,
        name: theme.name.trim(),
        slug,
        description: theme.description || null,
        status: theme.status || 'building',
        codebase_url: theme.codebase_url || getThemeCodebasePath(restaurantId, slug),
        updated_at: new Date().toISOString(),
      };

      let saved;
      let customError;

      if (theme.id) {
        ({ data: saved, error: customError } = await supabase
          .from('restaurant_custom_themes')
          .update(row)
          .eq('id', theme.id)
          .select('id')
          .single());
      } else {
        ({ data: saved, error: customError } = await supabase
          .from('restaurant_custom_themes')
          .insert(row)
          .select('id')
          .single());
      }

      if (customError) return { error: customError };

      const customId = saved?.id || theme.id;
      if (!customId) continue;

      const previewUrl = buildThemePreviewUrl(restaurantId, customId);
      const { error: previewUrlError } = await supabase
        .from('restaurant_custom_themes')
        .update({ preview_url: previewUrl, updated_at: new Date().toISOString() })
        .eq('id', customId);

      if (previewUrlError) return { error: previewUrlError };

      keptCustomIds.push(customId);

      const draftKey = theme.id || `new-${index}`;
      const enabled = Boolean(enabledCustom[draftKey]);
      const { error: optError } = await supabase.from('restaurant_theme_options').upsert(
        {
          restaurant_id: restaurantId,
          theme_kind: 'custom',
          theme_id: customId,
          enabled,
          is_default: defaultThemeType === 'custom' && defaultThemeId === customId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'restaurant_id,theme_kind,theme_id' },
      );

      if (optError) return { error: optError };
    }

    const { data: existingCustom } = await supabase
      .from('restaurant_custom_themes')
      .select('id')
      .eq('restaurant_id', restaurantId);

    const removeIds = (existingCustom || [])
      .map((r) => r.id)
      .filter((id) => !keptCustomIds.includes(id));

    if (removeIds.length > 0) {
      await supabase
        .from('restaurant_theme_options')
        .delete()
        .eq('restaurant_id', restaurantId)
        .in('theme_id', removeIds);
      await supabase.from('restaurant_custom_themes').delete().in('id', removeIds);
    }

    let resolvedDefaultId = defaultThemeId;
    const resolvedDefaultType = defaultThemeType;

    if (
      defaultThemeType === 'custom' &&
      defaultThemeId === this.CUSTOM_THEME_ID &&
      keptCustomIds[0]
    ) {
      resolvedDefaultId = keptCustomIds[0];
    }

    const { error: restaurantError } = await supabase
      .from('restaurants')
      .update({
        theme_id: resolvedDefaultId,
        theme_type: resolvedDefaultType,
        updated_at: new Date().toISOString(),
      })
      .eq('id', restaurantId);

    return { error: restaurantError || null };
  }
}

// Instantiate singleton
export const themeService = new ThemeService();

// Backwards-compatible bindings
export const getThemeOptionsForRestaurant =
  themeService.getThemeOptionsForRestaurant.bind(themeService);
export const isThemeSelectionAllowed = themeService.isThemeSelectionAllowed.bind(themeService);
export const saveRestaurantThemeSelection =
  themeService.saveRestaurantThemeSelection.bind(themeService);
export const getCustomThemeSlug = themeService.getCustomThemeSlug.bind(themeService);
export const buildDefaultThemeAdminState =
  themeService.buildDefaultThemeAdminState.bind(themeService);
export const getRestaurantThemeAdminState =
  themeService.getRestaurantThemeAdminState.bind(themeService);
export const saveRestaurantThemeAdminState =
  themeService.saveRestaurantThemeAdminState.bind(themeService);

export default themeService;
